// Viti Vinci — Header behaviour: open/close overlay, category switching, live cart count.

class VVHeader extends HTMLElement {
  connectedCallback() {
    // Set --header-height on <body> so dependent layouts (e.g. hero) can
    // pull themselves up by exactly the nav's rendered height. Horizon's
    // own JS only targets <header-component>, so we set this ourselves.
    this.setHeaderHeight();
    new ResizeObserver(() => this.setHeaderHeight()).observe(this);

    // Build the categories nav from rendered panels + markers.
    // (Shopify OS 2.0 theme-blocks aren't iterable from section.blocks, so we
    //  read the data the block files emitted into the panels container.)
    this.buildCategoriesNav();

    this.openButtons = this.querySelectorAll('[data-vv-menu-open]');
    this.closeButtons = this.querySelectorAll('[data-vv-menu-close]');
    this.categoryButtons = this.querySelectorAll('[data-vv-category]');
    this.panels = this.querySelectorAll('[data-vv-panel]');
    this.cartCounts = this.querySelectorAll('[data-vv-cart-count]');

    this.openButtons.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); this.open(); }));
    this.closeButtons.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); this.close(); }));
    this.categoryButtons.forEach((b) => b.addEventListener('click', (e) => this.handleCategory(e, b)));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dataset.menuOpen === 'true') this.close();
    });

    // Live cart count
    document.addEventListener('cart:update', (e) => {
      const count = e.detail?.resource?.item_count ?? e.detail?.itemCount;
      if (typeof count === 'number') this.updateCartCount(count);
    });

    // Mobile: toggle panels inline (EDITIONS expand/collapse)
    this.querySelectorAll('[data-vv-panel-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      });
    });

    // Scroll-aware background — gradient pages (transparent header)
    // become solid cream once the hero scrolls past the nav.
    this.setupScrollAwareBg();
  }

  setupScrollAwareBg() {
    // Only applies on pages set to gradient mode (header is transparent
    // initially, becomes solid once the hero scrolls past the nav).
    if (this.dataset.gradient !== 'true') return;

    const main =
      document.querySelector('main#MainContent') ||
      document.querySelector('main') ||
      document.querySelector('#MainContent');
    if (!main) return;

    // Find the actual hero element. Convention across VV pages:
    //   .vv-*__hero (homepage, PDP, studio, collection, installations,
    //   commissions, contact, journal/article).
    // If none exists (e.g. policy / blog listing without hero image),
    // the header should stay solid permanently — set scrolled=true and
    // skip the scroll listener entirely.
    const hero = this.findHeroSentinel(main);
    if (!hero) {
      this.dataset.scrolled = 'true';
      return;
    }

    const apply = () => {
      const navHeight = this.offsetHeight || 80;
      const heroBottom = hero.getBoundingClientRect().bottom;
      // Flip when hero's bottom edge has crossed the nav's bottom edge.
      this.dataset.scrolled = heroBottom <= navHeight + 1 ? 'true' : 'false';
    };

    apply();
    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          apply();
          ticking = false;
        });
      },
      { passive: true }
    );
    window.addEventListener('resize', apply);
  }

  findHeroSentinel(main) {
    // Look for any element whose class matches the hero conventions:
    //   - "vv-hero" (standalone homepage hero section)
    //   - "*__hero" (vv-studio__hero, vv-collection__hero, etc.)
    //   - "vv-product__gallery" (PDP hero slot — product image area)
    // Skip child elements like "__hero-img" / "__hero-media".
    const candidates = main.querySelectorAll(
      '[class~="vv-hero"], [class*="__hero"], .vv-product__gallery'
    );
    for (const el of candidates) {
      const hasBareHero = Array.from(el.classList).some(
        (c) =>
          c === 'vv-hero' ||
          c === 'vv-product__gallery' ||
          /__hero$/.test(c)
      );
      if (hasBareHero) return el;
    }
    return null;
  }

  setHeaderHeight() {
    const bar = this.querySelector('.vv-header__bar');
    if (!bar) return;
    const h = bar.offsetHeight || 80;
    document.body.style.setProperty('--header-height', `${h}px`);
    document.documentElement.style.setProperty('--header-height', `${h}px`);
  }

  buildCategoriesNav() {
    const nav = this.querySelector('[data-vv-categories]');
    const panelsContainer = this.querySelector('.vv-header__panels');
    if (!nav || !panelsContainer) return;

    // Collect every category in DOM order: panels (have nested items) + markers (link-only)
    const items = panelsContainer.querySelectorAll('[data-vv-panel], [data-vv-category-marker]');
    items.forEach((item) => {
      const label = item.dataset.vvLabel || '';
      if (!label) return;

      if (item.dataset.vvPanel) {
        // Panel-backed category — render as a button that toggles the panel
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'vv-header__category';
        btn.dataset.vvCategory = item.dataset.vvPanel;
        btn.innerHTML = `<h5 class="vv-header__category-label">${label}</h5>`;
        nav.appendChild(btn);
      } else {
        // Marker-only — render as a direct link
        const a = document.createElement('a');
        a.className = 'vv-header__category';
        a.href = item.dataset.vvLink || '#';
        a.innerHTML = `<h5 class="vv-header__category-label">${label}</h5>`;
        nav.appendChild(a);
      }
    });
  }

  activatePanel(id) {
    this.panels.forEach((p) => p.dataset.active = (p.dataset.vvPanel === id ? 'true' : 'false'));
    this.categoryButtons.forEach((b) => {
      if (b.dataset.vvCategory === id) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
  }

  open() {
    this.dataset.menuOpen = 'true';
    document.documentElement.dataset.vvMenuOpen = 'true';
  }

  close() {
    this.dataset.menuOpen = 'false';
    document.documentElement.dataset.vvMenuOpen = 'false';
    // Reset panels so the next menu open starts with everything collapsed
    this.panels.forEach((p) => p.dataset.active = 'false');
    this.categoryButtons.forEach((b) => b.removeAttribute('aria-current'));
  }

  handleCategory(e, button) {
    const target = button.dataset.vvCategory;
    const hasPanel = this.querySelector(`[data-vv-panel="${target}"]`);
    if (!hasPanel) return;  // simple link → let browser navigate
    e.preventDefault();
    this.activatePanel(target);
  }

  updateCartCount(n) {
    this.cartCounts.forEach((el) => { el.textContent = n; });
  }
}

if (!customElements.get('vv-header')) {
  customElements.define('vv-header', VVHeader);
}
